import { computed, onUnmounted, ref } from "vue";
import { debouncedWrite } from "@/utils/debouncedWrite";
export const NEW_COVER_TEMPLATE = "new-cover-sheet";
const SAVED_COVER_TEMPLATE = "saved-cover-sheet";
const MAX_COVER_TEMPLATE_PAGES = 20;
const SAVE_DEBOUNCE_MS = 1000;
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
/**
 * Cover sheet state machine shared by the order-page appeal letter and the self-serve Appeals page:
 * the company's templates, the AI fill and its poll, the user's edits, and the creation of a new
 * template. The superseded-fill guard, the debounce and the dropdown items live here.
 */
export function useAppealCoverSheet({ fetchTemplatePage, startFill, fetchFill, currentTarget, saveText, createTemplate, onError, }) {
    const coverTemplates = ref([]);
    const coverTemplatesLoading = ref(false);
    const coverSheetText = ref("");
    const coverTemplateId = ref(null);
    const coverFilling = ref(false);
    const coverDialogOpen = ref(false);
    const coverSaving = ref(false);
    const coverDialogError = ref("");
    let pollTimer = null;
    let coverGeneration = 0;
    const coverWrite = debouncedWrite(SAVE_DEBOUNCE_MS);
    const coverTemplateItems = computed(() => [
        { title: "No cover sheet", value: null },
        ...(coverTemplateId.value === SAVED_COVER_TEMPLATE
            ? [{ title: "Saved cover sheet", value: SAVED_COVER_TEMPLATE }]
            : []),
        ...coverTemplates.value.map((template) => ({ title: template.name, value: template.id })),
        { title: "+ New cover sheet…", value: NEW_COVER_TEMPLATE },
    ]);
    const clearPoll = () => {
        coverGeneration += 1;
        if (pollTimer) {
            clearTimeout(pollTimer);
            pollTimer = null;
        }
    };
    const persistCoverText = (text, target) => saveText(text, target).catch((e) => onError(`Failed to save draft: ${e.message || e}`));
    /** The queue can run a write after a navigation, so the target is the letter parent on screen now. */
    const scheduleCoverText = (text) => {
        const target = currentTarget();
        coverWrite.schedule(() => persistCoverText(text, target));
    };
    /** Replaces the whole text now, behind whatever write is already in flight. */
    const saveCoverText = (text) => {
        scheduleCoverText(text);
        return coverWrite.flush();
    };
    /** A debounced save still in flight is the user's last keystrokes: send it rather than drop it. */
    const flushPendingSave = () => coverWrite.flush();
    const seedCoverText = (text) => {
        coverSheetText.value = text || "";
        coverTemplateId.value = coverSheetText.value ? SAVED_COVER_TEMPLATE : null;
    };
    const loadCoverTemplates = async () => {
        coverTemplatesLoading.value = true;
        try {
            const loaded = [];
            for (let page = 1; page <= MAX_COVER_TEMPLATE_PAGES; page += 1) {
                const response = await fetchTemplatePage(page);
                loaded.push(...(response?.results || []));
                if (!response?.next)
                    break;
            }
            coverTemplates.value = loaded;
        }
        catch (e) {
            onError(`Failed to load the cover sheets: ${e.message || e}`);
        }
        finally {
            coverTemplatesLoading.value = false;
        }
    };
    const pollFill = (taskId, generation) => {
        const startedAt = Date.now();
        const fail = (message) => {
            onError(message);
            coverFilling.value = false;
            coverTemplateId.value = null;
        };
        const tick = () => {
            pollTimer = setTimeout(async () => {
                try {
                    const result = await fetchFill(taskId);
                    if (generation !== coverGeneration)
                        return;
                    if (result.status === "done") {
                        coverSheetText.value = result.coverSheetText || "";
                        coverFilling.value = false;
                        return;
                    }
                    if (result.status === "error") {
                        fail(result.error || "Filling the cover sheet failed. Please try again.");
                        return;
                    }
                    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                        fail("Filling the cover sheet timed out. Please try again.");
                        return;
                    }
                    tick();
                }
                catch (e) {
                    if (generation !== coverGeneration)
                        return;
                    fail(`Failed to fetch the filled cover sheet: ${e.message || e}`);
                }
            }, POLL_INTERVAL_MS);
        };
        tick();
    };
    const startCoverFill = async (templateId) => {
        onError("");
        coverSheetText.value = "";
        coverFilling.value = true;
        clearPoll();
        const generation = coverGeneration;
        try {
            await saveCoverText("");
            const { taskId } = await startFill(templateId);
            pollFill(taskId, generation);
        }
        catch (e) {
            coverFilling.value = false;
            coverTemplateId.value = null;
            onError(`Failed to start the cover sheet fill: ${e.message || e}`);
        }
    };
    const openCoverDialog = () => {
        coverDialogError.value = "";
        coverDialogOpen.value = true;
    };
    // The dropdown's last item is an action rather than a template: it opens the creation dialog and
    // leaves the current selection in place.
    const onCoverTemplateSelected = (templateId) => {
        if (templateId === NEW_COVER_TEMPLATE) {
            openCoverDialog();
            return;
        }
        if (templateId === SAVED_COVER_TEMPLATE)
            return;
        coverTemplateId.value = templateId;
        clearPoll();
        coverFilling.value = false;
        if (!templateId) {
            coverSheetText.value = "";
            saveCoverText("");
            return;
        }
        startCoverFill(templateId);
    };
    const onCoverTextEdited = (value) => {
        coverSheetText.value = value;
        scheduleCoverText(value);
    };
    const saveCoverTemplate = async (form) => {
        coverDialogError.value = "";
        coverSaving.value = true;
        const generation = coverGeneration;
        try {
            const created = await createTemplate(form);
            if (generation !== coverGeneration)
                return;
            coverDialogOpen.value = false;
            await loadCoverTemplates();
            if (generation !== coverGeneration)
                return;
            coverTemplateId.value = created.id;
            startCoverFill(created.id);
        }
        catch (e) {
            coverDialogError.value = e.message || String(e);
        }
        finally {
            coverSaving.value = false;
        }
    };
    const resetCoverSheet = () => {
        clearPoll();
        coverWrite.cancel();
        coverFilling.value = false;
        coverSheetText.value = "";
    };
    onUnmounted(() => {
        clearPoll();
        flushPendingSave();
    });
    return {
        coverSheetText,
        coverTemplateId,
        coverTemplateItems,
        coverTemplatesLoading,
        coverFilling,
        coverDialogOpen,
        coverSaving,
        coverDialogError,
        seedCoverText,
        resetCoverSheet,
        flushPendingSave,
        loadCoverTemplates,
        onCoverTemplateSelected,
        onCoverTextEdited,
        saveCoverTemplate,
    };
}
